import React, { useMemo } from "react";
import { Mafs, Coordinates, Polygon, Line, Point, Text, Theme } from "mafs";

export const BoxPlot = ({ config = {}, variables = {} }) => {
    const {
        stats: rawStats = {},
        outliers: rawOutliers = [],
        axisLabel: rawAxisLabel,
    } = config;

    // --- Robust parsing helpers (prevents NaN/Infinity from blanking the Mafs viewBox) ---
    const toFinite = (v, fallback = 0) => {
        const n = Number(v);
        return Number.isFinite(n) ? n : fallback;
    };

    // Resolve values (Variables > Config.stats > fallback), but always finite.
    const resolve = (key, fallback) => {
        const val = variables?.[key] ?? rawStats?.[key] ?? fallback;
        return toFinite(val, fallback);
    };

    const stats = useMemo(() => {
        const min = resolve("min", 0);
        const q1 = resolve("q1", 0);
        const med = resolve("median", 0); // matches Java field name: "median"
        const q3 = resolve("q3", 0);
        const max = resolve("max", 0);

        // Safety sort (also keeps the widget usable if user-provided variables are inconsistent)
        const sorted = [min, q1, med, q3, max].sort((a, b) => a - b);

        return {
            min: sorted[0],
            q1: sorted[1],
            med: sorted[2],
            q3: sorted[3],
            max: sorted[4],
        };
    }, [rawStats, variables]);

    const outliers = useMemo(() => {
        return (rawOutliers || [])
            .map((v) => Number(v))
            .filter(Number.isFinite);
    }, [rawOutliers]);

    const axisLabel =
        typeof rawAxisLabel === "string" && rawAxisLabel.trim()
            ? rawAxisLabel
            : "Value";

    // --- Dynamic viewBox (finite-safe) ---
    const { rangeMin, rangeMax, padding } = useMemo(() => {
        const all = [stats.min, stats.max, ...outliers].filter(Number.isFinite);

        // Fallback range if somehow everything is non-finite
        if (all.length === 0) {
            return { rangeMin: -10, rangeMax: 10, padding: 2 };
        }

        const min = Math.min(...all);
        const max = Math.max(...all);

        const spread = max - min;
        const safeSpread = spread === 0 ? 10 : spread;
        const pad = safeSpread * 0.2;

        return { rangeMin: min, rangeMax: max, padding: pad };
    }, [stats, outliers]);

    const bottom = -0.6;
    const top = 0.6;

    return (
        <Mafs
            viewBox={{ x: [rangeMin - padding, rangeMax + padding], y: [-1.5, 1.5] }}
            pan={false}
            height={200}
            preserveAspectRatio={false}
        >
            <Coordinates.Cartesian
                yAxis={{ lines: false, labels: false }}
                xAxis={{ lines: true, labels: (n) => n }}
            />

            {/* Whiskers */}
            <Line.Segment point1={[stats.min, 0]} point2={[stats.q1, 0]} color={Theme.indigo} />
            <Line.Segment point1={[stats.q3, 0]} point2={[stats.max, 0]} color={Theme.indigo} />

            {/* End Caps */}
            <Line.Segment point1={[stats.min, -0.3]} point2={[stats.min, 0.3]} color={Theme.indigo} />
            <Line.Segment point1={[stats.max, -0.3]} point2={[stats.max, 0.3]} color={Theme.indigo} />

            {/* IQR Box */}
            <Polygon
                points={[
                    [stats.q1, bottom],
                    [stats.q3, bottom],
                    [stats.q3, top],
                    [stats.q1, top],
                ]}
                color={Theme.indigo}
                fillOpacity={0.1}
                strokeWidth={2}
            />

            {/* Median Line */}
            <Line.Segment
                point1={[stats.med, bottom]}
                point2={[stats.med, top]}
                color={Theme.indigo}
                weight={4}
            />
            <Text x={stats.med} y={top + 0.4} attach="s" size={16} weight="bold">
                Median
            </Text>

            {/* Outliers */}
            {outliers.map((val, i) => (
                <Point
                    key={`outlier-${i}`}
                    x={val}
                    y={0}
                    color={Theme.red}
                    opacity={0.8}
                />
            ))}

            {/* Axis Label */}
            <Text x={(rangeMin + rangeMax) / 2} y={-1.2} size={14}>
                {axisLabel}
            </Text>
        </Mafs>
    );
};
